import { useState } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage, extractErrorCode } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import { REGENERATE_NOT_PERMITTED_CODE } from '../utils/dashboardOwnership'
import { isAiRefusalCode } from '../utils/aiAccess'
import {
  MultiTableAnalysis,
  MultiTableAnalysisItem,
  MultiTableAnalysisProgress,
} from '../types/gotypes'

interface StartAnalysisRequest {
  persona?: string
  user_context?: string
  synthesis_user_context?: string
  output_length?: string
  force?: boolean
  analysis_instructions?: string
  synthesis_instructions?: string
  /** Raw text of the user's manual edit, folded into the regeneration's
   *  synthesis prompt server-side when they chose "Include my edit" (#1144). */
  edit_fold_in_context?: string
  /** The dashboard filter for each job the card's tables come from (#1810).
   *  The server keeps only the jobs the card's tables use and snapshots it. */
  job_filters?: Record<string, string>
}

interface StartAnalysisResponse {
  message: string
  analysis: MultiTableAnalysis
}

interface ListAnalysesResponse {
  analyses: MultiTableAnalysis[]
  count: number
}

interface ProgressResponse {
  id: string
  state: string
  error?: string
  progress?: MultiTableAnalysisProgress
}

interface ListItemsResponse {
  items: MultiTableAnalysisItem[]
  count: number
}

interface UseMultiTableAnalysisOptions {
  customer: string
  job: string
  multiTableId: string
  analysisId?: string // For single analysis operations
}

export const useMultiTableAnalysis = (options: UseMultiTableAnalysisOptions) => {
  const { customer, job, multiTableId, analysisId } = options

  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | undefined>(analysisId)

  // Query keys
  const listQueryKey = ['multi_table_analyses', { customer, job, multiTableId }]
  const analysisQueryKey = ['multi_table_analysis', { customer, job, multiTableId, analysisId: selectedAnalysisId }]
  const progressQueryKey = ['multi_table_analysis_progress', { analysisId: selectedAnalysisId }]
  const itemsQueryKey = ['multi_table_analysis_items', { analysisId: selectedAnalysisId }]

  // ============ List Analyses ============
  const analysesQuery = useQuery({
    queryKey: listQueryKey,
    enabled: Boolean(customer && job && multiTableId && account.user),
    refetchInterval: (query) => {
      // Poll the analyses list while any analysis is running/pending
      const data = query.state.data as MultiTableAnalysis[] | undefined
      if (data?.some(a => a.state === 'running' || a.state === 'pending')) {
        return 3000 // 3 seconds
      }
      // Also poll if no completed analysis found yet (newly triggered analysis may not appear yet)
      if (data && data.length === 0) {
        return 5000 // 5 seconds - slower poll when waiting for first analysis to appear
      }
      return false
    },
    queryFn: async () => {
      if (!customer || !job || !multiTableId || !account.user) return []
      try {
        const res = await axios.get<ListAnalysesResponse>(
          `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/analysis`
        )
        return res.data.analyses || []
      } catch (err) {
        console.error('[useMultiTableAnalysis] Failed to fetch analyses:', err)
        return []
      }
    },
  })

  // ============ Get Single Analysis ============
  const analysisQuery = useQuery({
    queryKey: analysisQueryKey,
    enabled: Boolean(customer && job && multiTableId && selectedAnalysisId && account.user),
    refetchInterval: (query) => {
      // Poll while running or pending
      const data = query.state.data as MultiTableAnalysis | undefined
      if (data?.state === 'running' || data?.state === 'pending') {
        return 2000 // 2 seconds
      }
      return false
    },
    queryFn: async () => {
      if (!customer || !job || !multiTableId || !selectedAnalysisId || !account.user) {
        return null
      }
      try {
        const res = await axios.get<MultiTableAnalysis>(
          `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/analysis/${selectedAnalysisId}`
        )
        return res.data
      } catch (err) {
        console.error('[useMultiTableAnalysis] Failed to fetch analysis:', err)
        return null
      }
    },
  })

  // ============ Get Progress (lightweight polling) ============
  const progressQuery = useQuery({
    queryKey: progressQueryKey,
    enabled: Boolean(customer && job && multiTableId && selectedAnalysisId && account.user),
    refetchInterval: (query) => {
      const data = query.state.data as ProgressResponse | undefined
      if (data?.state === 'running' || data?.state === 'pending') {
        return 1500 // 1.5 seconds for more responsive progress
      }
      return false
    },
    queryFn: async () => {
      if (!customer || !job || !multiTableId || !selectedAnalysisId || !account.user) {
        return null
      }
      try {
        const res = await axios.get<ProgressResponse>(
          `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/analysis/${selectedAnalysisId}/progress`
        )
        return res.data
      } catch (err) {
        console.error('[useMultiTableAnalysis] Failed to fetch progress:', err)
        return null
      }
    },
  })

  // ============ Get Analysis Items ============
  const itemsQuery = useQuery({
    queryKey: itemsQueryKey,
    enabled: Boolean(customer && job && multiTableId && selectedAnalysisId && account.user),
    refetchInterval: (query) => {
      // Get the current analysis state from the analysisQuery
      const analysisData = queryClient.getQueryData<MultiTableAnalysis>(analysisQueryKey)
      const analysisState = analysisData?.state

      // Poll while analysis is running or pending
      if (analysisState === 'running' || analysisState === 'pending') {
        return 2000 // 2 seconds
      }

      // Also poll if analysis is completed but items haven't loaded yet
      // This handles the race condition where items are fetched before completion
      const items = query.state.data as MultiTableAnalysisItem[] | undefined
      if (analysisState === 'completed' && items) {
        const hasIncompleteItems = items.some(item =>
          item.state === 'pending' || item.state === 'running'
        )
        if (hasIncompleteItems) {
          return 1000 // 1 second - faster polling to catch completion
        }
      }

      return false
    },
    queryFn: async () => {
      if (!customer || !job || !multiTableId || !selectedAnalysisId || !account.user) {
        return []
      }
      try {
        const res = await axios.get<ListItemsResponse>(
          `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/analysis/${selectedAnalysisId}/items`
        )
        return res.data.items || []
      } catch (err) {
        console.error('[useMultiTableAnalysis] Failed to fetch items:', err)
        return []
      }
    },
  })

  // ============ Start Analysis ============
  const startAnalysisMutation = useMutation({
    mutationFn: async (request: StartAnalysisRequest) => {
      if (!customer || !job || !multiTableId || !account.user) {
        throw new Error('Missing required parameters')
      }
      const res = await axios.post<StartAnalysisResponse>(
        `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/analysis/start`,
        request
      )
      return res.data
    },
    onSuccess: (data) => {
      snackbar.success('Analysis started')
      // Immediately add the new analysis to the list cache so hasActiveAnalysis
      // becomes true before the next poll (avoids isRegenerating race condition)
      queryClient.setQueryData(listQueryKey, (old: MultiTableAnalysis[] | undefined) => {
        return [data.analysis, ...(old || [])]
      })
      // Select the new analysis
      setSelectedAnalysisId(data.analysis.id)
      // Invalidate queries to refresh lists
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (err) => {
      const message = extractErrorMessage(err)
      // #1219 gate 2: the server's own wording already explains the refusal
      // plainly ("you don't own this dashboard...") — prefixing it with
      // "Failed to start analysis" makes a permission decision read like a
      // bug. The widget also renders this text directly in the card body.
      // The same goes for the AI access refusals (ai_access_level / ai_budget):
      // their sentence is the whole message (audit A03-12).
      const code = extractErrorCode(err)
      if (code === REGENERATE_NOT_PERMITTED_CODE || isAiRefusalCode(code)) {
        snackbar.error(message)
      } else {
        snackbar.error(`Failed to start analysis: ${message}`)
      }
    },
  })

  // ============ Delete Analysis ============
  const deleteAnalysisMutation = useMutation({
    mutationFn: async (analysisIdToDelete: string) => {
      if (!customer || !job || !multiTableId || !account.user) {
        throw new Error('Missing required parameters')
      }
      await axios.delete(
        `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/analysis/${analysisIdToDelete}`
      )
    },
    onSuccess: () => {
      snackbar.success('Analysis deleted')
      // Clear selection if we deleted the selected analysis
      if (selectedAnalysisId) {
        setSelectedAnalysisId(undefined)
      }
      // Invalidate queries to refresh lists
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (err) => {
      const message = extractErrorMessage(err)
      snackbar.error(`Failed to delete analysis: ${message}`)
    },
  })

  // ============ Retry Item ============
  const retryItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      if (!customer || !job || !multiTableId || !selectedAnalysisId || !account.user) {
        throw new Error('Missing required parameters')
      }
      const res = await axios.post(
        `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/analysis/${selectedAnalysisId}/items/${itemId}/retry`
      )
      return res.data
    },
    onSuccess: () => {
      snackbar.success('Retrying table analysis')
      queryClient.invalidateQueries({ queryKey: itemsQueryKey })
      queryClient.invalidateQueries({ queryKey: progressQueryKey })
      queryClient.invalidateQueries({ queryKey: analysisQueryKey })
    },
    onError: (err) => {
      const message = extractErrorMessage(err)
      snackbar.error(`Failed to retry: ${message}`)
    },
  })

  // ============ Computed State ============
  const currentAnalysis = analysisQuery.data ?? null
  const progress = progressQuery.data?.progress ?? currentAnalysis?.progress ?? null
  const isRunning = currentAnalysis?.state === 'running' || currentAnalysis?.state === 'pending'
  const isCompleted = currentAnalysis?.state === 'completed'
  const isFailed = currentAnalysis?.state === 'failed'
  const items = itemsQuery.data ?? []
  const analyses = analysesQuery.data ?? []

  // ============ Persona Helpers ============
  // Find a completed analysis for a specific persona
  const findAnalysisByPersona = (persona: string): MultiTableAnalysis | undefined => {
    return analyses.find(a => a.persona === persona && a.state === 'completed')
  }

  // Get all available personas that have completed analyses
  const getAvailablePersonas = (): string[] => {
    return [...new Set(analyses.filter(a => a.state === 'completed').map(a => a.persona))]
  }

  // Switch to an analysis for a given persona, or return false if none exists
  const switchToPersona = (persona: string): boolean => {
    const analysis = findAnalysisByPersona(persona)
    if (analysis) {
      setSelectedAnalysisId(analysis.id)
      return true
    }
    return false
  }

  return {
    // State
    selectedAnalysisId,
    setSelectedAnalysisId,

    // List operations
    analyses: analysesQuery.data ?? [],
    analysesLoading: analysesQuery.isLoading,

    // Single analysis operations
    analysis: currentAnalysis,
    analysisLoading: analysisQuery.isLoading,

    // Progress
    progress,
    progressLoading: progressQuery.isLoading,

    // Items
    items,
    itemsLoading: itemsQuery.isLoading,

    // Computed state
    isRunning,
    isCompleted,
    isFailed,

    // Mutations
    startAnalysis: startAnalysisMutation.mutateAsync,
    startAnalysisLoading: startAnalysisMutation.isPending,
    deleteAnalysis: deleteAnalysisMutation.mutateAsync,
    deleteAnalysisLoading: deleteAnalysisMutation.isPending,
    retryItem: retryItemMutation.mutateAsync,
    retryItemLoading: retryItemMutation.isPending,

    // Refresh functions
    refreshAnalyses: () => queryClient.invalidateQueries({ queryKey: listQueryKey }),
    refreshAnalysis: () => queryClient.invalidateQueries({ queryKey: analysisQueryKey }),
    refreshProgress: () => queryClient.invalidateQueries({ queryKey: progressQueryKey }),
    refreshItems: () => queryClient.invalidateQueries({ queryKey: itemsQueryKey }),

    // Persona helpers
    findAnalysisByPersona,
    getAvailablePersonas,
    switchToPersona,
  }
}

export default useMultiTableAnalysis
